import React, { useState } from 'react';
import { Product, Category } from '@/domain/entities';
import { ImageModal } from './ImageModal';

interface ProductTableProps {
  products: Product[];
  categories: Category[];
  onEdit: (product: Product) => void;
  onDelete: (sku: string) => void;
  onPreview?: (product: Product) => void;
}

export const ProductTable: React.FC<ProductTableProps> = ({
  products,
  categories,
  onEdit,
  onDelete,
  onPreview,
}) => {
  const [selectedImage, setSelectedImage] = useState<{ url: string; alt: string } | null>(null);

  const getCategoryName = (categoryId: number): string => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category?.nameRu || String(categoryId);
  };
  if (!products || products.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow ring-1 ring-black ring-opacity-5 dark:ring-gray-700 rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
        Товары не найдены
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                <tr>
                  <th className="py-2.5 px-4 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider w-32 border-b border-r border-gray-300 dark:border-gray-600">
                    SKU
                  </th>
                  <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                    Название
                  </th>
                  <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                    Бренд
                  </th>
                  <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                    Категория
                  </th>
                  <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                    Цвет
                  </th>
                  <th className="py-2.5 px-3 text-right text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-300 dark:border-gray-600">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800">
                {products.map((product) => (
                  <tr key={product.sku} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <td className="whitespace-nowrap py-2.5 px-4 text-sm font-medium text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        {product.imageUrl && (
                          <img
                            src={product.imageUrl}
                            alt={product.name}
                            className="h-12 w-12 object-cover rounded border border-gray-200 dark:border-gray-600 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => setSelectedImage({ url: product.imageUrl, alt: product.name })}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        )}
                        <span>{product.sku}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                      {product.name}
                    </td>
                    <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                      {product.brand}
                    </td>
                    <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                      {getCategoryName(product.categoryId)}
                    </td>
                    <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                      {product.color}
                    </td>
                    <td className="whitespace-nowrap py-2.5 px-3 text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {onPreview && (
                          <button
                            onClick={() => onPreview(product)}
                            className="px-3 py-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                          >
                            Редактировать
                          </button>
                        )}
                        <button
                          onClick={() => onDelete(product.sku)}
                          className="px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                        >
                          Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

      {selectedImage && (
        <ImageModal
          imageUrl={selectedImage.url}
          alt={selectedImage.alt}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </div>
  );
};

